import React from "react";
import LegalPage, { Section } from "@/components/LegalPage";
import { business } from "@/lib/business";

export default function Refund() {
  return (
    <LegalPage
      title="Refund & Return Policy"
      subtitle={`Straight-forward returns. ${business.returnWindowDays} days from delivery, no restocking fee for defective items.`}
      testId="refund-page"
    >
      <p>
        We want you to love what you bought. If for any reason you don't, you can return most items
        within {business.returnWindowDays} days of delivery for a full refund. This page walks you
        through exactly how it works.
      </p>

      <Section n="1" title="Return window">
        <p>
          You have <strong>{business.returnWindowDays} calendar days</strong> from the day your order
          was delivered (per the carrier's tracking) to request a return. Requests submitted after that
          window will not be accepted.
        </p>
      </Section>

      <Section n="2" title="Condition requirements">
        <p>To be eligible for a full refund, the item must be:</p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>Unused, in original condition (no scratches, marks, or missing pieces)</li>
          <li>In the original packaging with all tags, manuals and accessories</li>
          <li>Accompanied by proof of purchase (order confirmation email or order ID)</li>
        </ul>
        <p>
          Items returned in reduced condition may receive a partial refund at our discretion (typically
          70-90% of the purchase price).
        </p>
      </Section>

      <Section n="3" title="Items we cannot accept back">
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>In-ear headphones or earbuds where the seal has been broken (hygiene reasons)</li>
          <li>Items marked "Final Sale" at checkout</li>
          <li>Gift cards, downloadable digital products, or personalized items</li>
          <li>Items returned more than {business.returnWindowDays} days after delivery</li>
        </ul>
      </Section>

      <Section n="4" title="How to start a return">
        <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
          <li>
            Email <a href={`mailto:${business.supportEmail}`} className="text-primary underline">{business.supportEmail}</a>{" "}
            with your order ID and reason for the return. Photos help if the item is damaged.
          </li>
          <li>
            We will reply within 2 business days with a Return Authorization number (RA#) and shipping
            instructions.
          </li>
          <li>
            Pack the item securely (original packaging is best), write the RA# on the outside of the
            package, and drop it off with the carrier.
          </li>
          <li>
            Once received and inspected (typically 3-5 business days), we issue your refund.
          </li>
        </ol>
      </Section>

      <Section n="5" title="Return shipping cost">
        <p>
          <strong>Defective or wrong item?</strong> We cover return shipping — we'll email you a
          prepaid label.
        </p>
        <p>
          <strong>Changed your mind?</strong> You pay return shipping. We recommend a trackable service;
          we're not responsible for items lost on the way back.
        </p>
      </Section>

      <Section n="6" title="Refund timing & method">
        <p>
          Approved refunds go back to the original payment method (Stripe or PayPal). Please allow:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>3-5 business days for us to inspect and process</li>
          <li>2-10 additional business days for your bank or PayPal to make the funds available</li>
        </ul>
        <p>
          If you paid partly with store credit or a coupon, that portion is refunded as store credit and
          the rest to your original payment method.
        </p>
      </Section>

      <Section n="7" title="Exchanges">
        <p>
          The fastest way to get a different item is to return the original for a refund and place a new
          order. This avoids inventory holds and gets your new item to you sooner.
        </p>
      </Section>

      <Section n="8" title="Damaged or defective items">
        <p>
          If your item arrived damaged, defective, or is not what you ordered:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>Take photos of the item and packaging within 48 hours of delivery</li>
          <li>Email us at <a href={`mailto:${business.supportEmail}`} className="text-primary underline">{business.supportEmail}</a> with photos + order ID</li>
          <li>We'll ship a free replacement or issue a full refund — your choice</li>
        </ul>
        <p>
          Every item is also covered by our {business.warrantyMonths}-month manufacturer warranty for
          defects appearing after normal use.
        </p>
      </Section>

      <Section n="9" title="Order cancellations">
        <p>
          You may cancel an order for a full refund as long as it has <strong>not yet shipped</strong>.
          Once the carrier has picked it up, treat it as a return using the process above. Email us
          immediately if you need to cancel.
        </p>
      </Section>

      <Section n="10" title="Chargebacks">
        <p>
          If you believe your order is fraudulent or unauthorized, please contact us first — we can
          almost always resolve it faster than a chargeback. Filing an unauthorized chargeback without
          contacting us first may result in your account being blocked.
        </p>
      </Section>

      <Section n="11" title="Contact">
        <p>
          Questions about a return? Email{" "}
          <a href={`mailto:${business.supportEmail}`} className="text-primary underline">
            {business.supportEmail}
          </a>{" "}
          — we usually respond within 24 hours.
        </p>
      </Section>
    </LegalPage>
  );
}
